import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// CalendarSearch — click-to-expand search bar that lives in both the
// Calendar header and the scheduler panel header.
//
// Earlier versions had keyboard shortcuts (slash to focus, ↑/↓ to
// navigate, ↵ to schedule, ⌘↵ for "Now") + a side-by-side SchedulePanel
// that opened on the right, + a scroll-to-card focus pattern for
// projects. Joe didn't like the hot keys. New flow is purely
// click-driven and stays in one place:
//
//   1. Type → grouped dropdown (Tasks, Projects).
//   2. Click a TASK row → the row expands inline showing task context
//      (project name, due date, assignees) and a Schedule form below
//      (smart-slot chips + length picker + custom datetime).
//   3. Click a PROJECT row → the row expands inline showing the
//      project's task list (like the Mgmt Assigned widget) so the
//      user can pick a task to schedule, plus an option at the bottom
//      to schedule a generic "work on this project" block.
//   4. Confirm schedules a TimeBlock and clears the search.
//
// No keyboard schedule shortcuts. The only key behavior left is Esc
// to collapse the expanded row, which is mouse-y enough.
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { notify } from "./Toast";
import TimeSelect from "./TimeSelect";
const KIND_LABEL = {
    task: "Task",
    one_off: "One-off task",
    meeting: "Meeting",
    project: "Project",
};
function localDateInput(d) {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function localTimeInput(d) {
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getHours())}:${p(d.getMinutes())}`;
}
// Next quarter-hour so the custom default doesn't show "right now, 38
// seconds past the minute". Same trick the overdue picker uses.
function nextQuarterHour() {
    const d = new Date();
    const mins = (Math.floor(d.getMinutes() / 15) + 1) * 15;
    d.setMinutes(mins, 0, 0);
    return d;
}
const DURATION_OPTIONS = [30, 60, 90, 120];
export default function CalendarSearch({ onScheduled, }) {
    const inputRef = useRef(null);
    const [query, setQuery] = useState("");
    const [hits, setHits] = useState([]);
    const [dropOpen, setDropOpen] = useState(false);
    // The currently-expanded hit. Null = dropdown shows the flat result
    // list. When non-null, the matching row stays visible and shows its
    // detail body + schedule form below. Other rows collapse to just
    // their title so the expanded view has room.
    const [expandedHit, setExpandedHit] = useState(null);
    // For project expansion — the fetched project tree (lazily pulled
    // when a project row is expanded). Keyed by project_id to cache
    // across multiple opens within the same search session.
    const [projectTrees, setProjectTrees] = useState({});
    const [projectLoading, setProjectLoading] = useState(false);
    // Inside an expanded project, the user can pick a task by clicking
    // its row. We then run the same schedule form but bound to that
    // task instead of the project itself. Stored separately from
    // expandedHit so collapsing the project back to its task list is
    // a one-click "back".
    const [pickedProjectTask, setPickedProjectTask] = useState(null);
    // Schedule form state — shared between task picks and project-task
    // picks. We re-init duration whenever the target changes.
    const [duration, setDuration] = useState(60);
    // Split into separate date + time so the time picker matches the
    // rest of the app (TimeSelect = 15-min dropdown, no native scroll).
    const [customDate, setCustomDate] = useState(localDateInput(nextQuarterHour()));
    const [customTime, setCustomTime] = useState(localTimeInput(nextQuarterHour()));
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [saving, setSaving] = useState(false);
    // Debounced search.
    useEffect(() => {
        if (!query.trim()) {
            setHits([]);
            return;
        }
        let cancelled = false;
        const t = window.setTimeout(async () => {
            try {
                const r = await api.get(`/users/me/schedulable-search?q=${encodeURIComponent(query)}`);
                if (!cancelled) {
                    setHits(r.hits || []);
                    setDropOpen(true);
                }
            }
            catch {
                if (!cancelled)
                    setHits([]);
            }
        }, 180);
        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [query]);
    // Reset duration + custom datetime + fetch top-N free slots whenever
    // the schedule target changes (a task pick, a project-task pick, or
    // a project pick for the generic project block).
    const scheduleTarget = pickedProjectTask
        ? {
            kind: "task",
            id: pickedProjectTask.id,
            title: pickedProjectTask.title,
            project_id: pickedProjectTask.project_id,
            project_name: expandedHit?.title ?? null,
            suggested_minutes: 60,
            suggested_block_type: "lockout",
        }
        : expandedHit && expandedHit.kind !== "project"
            ? expandedHit
            : null;
    const scheduleProject = !scheduleTarget && expandedHit?.kind === "project" ? expandedHit : null;
    useEffect(() => {
        if (!scheduleTarget && !scheduleProject) {
            setSlots([]);
            return;
        }
        const dur = (scheduleTarget?.suggested_minutes ||
            (scheduleProject ? 60 : 60)) || 60;
        setDuration(dur);
        const seed = nextQuarterHour();
        setCustomDate(localDateInput(seed));
        setCustomTime(localTimeInput(seed));
        let cancelled = false;
        (async () => {
            setLoadingSlots(true);
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
                const r = await api.get(`/users/me/free-slots-today?duration=${dur}&count=3&tz=${encodeURIComponent(tz)}`);
                if (!cancelled)
                    setSlots(r.slots);
            }
            catch {
                if (!cancelled)
                    setSlots([]);
            }
            finally {
                if (!cancelled)
                    setLoadingSlots(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        scheduleTarget?.id,
        scheduleTarget?.suggested_minutes,
        scheduleProject?.id,
    ]);
    // Outside-click closes the dropdown.
    //
    // Listen on mousedown rather than click. Reason: clicking an already-
    // expanded row collapses it (React re-render removes the expanded
    // body before the click event finishes). By the time a "click"
    // handler runs, e.target has been removed from the DOM and
    // root.contains(target) returns false — the handler then thinks the
    // click was outside and closes the whole dropdown. mousedown fires
    // before React's onClick + state updates, so e.target is still in
    // its original position. Same fix lots of dropdown libraries use.
    useEffect(() => {
        if (!dropOpen && !expandedHit)
            return;
        function onDoc(e) {
            const root = inputRef.current?.closest(".cal-search-root");
            if (root && root.contains(e.target))
                return;
            setDropOpen(false);
            setExpandedHit(null);
            setPickedProjectTask(null);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [dropOpen, expandedHit]);
    // Lazy-fetch the project tree on expand.
    useEffect(() => {
        if (!expandedHit || expandedHit.kind !== "project")
            return;
        if (projectTrees[expandedHit.id])
            return;
        let cancelled = false;
        (async () => {
            setProjectLoading(true);
            try {
                const tree = await api.get(`/projects/${expandedHit.id}`);
                if (!cancelled) {
                    setProjectTrees((prev) => ({ ...prev, [expandedHit.id]: tree }));
                }
            }
            catch {
                /* leave projectTrees absent so the expanded body shows an empty state */
            }
            finally {
                if (!cancelled)
                    setProjectLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [expandedHit, projectTrees]);
    function expandHit(hit) {
        if (expandedHit?.id === hit.id && expandedHit.kind === hit.kind) {
            // Collapse if already expanded.
            setExpandedHit(null);
            setPickedProjectTask(null);
            return;
        }
        setExpandedHit(hit);
        setPickedProjectTask(null);
    }
    async function scheduleAt(startIso) {
        if (!scheduleTarget && !scheduleProject)
            return;
        setSaving(true);
        try {
            const start = new Date(startIso);
            const end = new Date(start.getTime() + duration * 60_000);
            const target = scheduleTarget ?? scheduleProject;
            const title = target.project_name
                ? `${target.project_name} — ${target.title}`
                : target.title;
            const body = {
                title,
                start_at: start.toISOString(),
                end_at: end.toISOString(),
                block_type: scheduleTarget?.suggested_block_type ||
                    "lockout",
            };
            if (scheduleTarget) {
                body.task_id =
                    scheduleTarget.kind === "task" ||
                        scheduleTarget.kind === "one_off" ||
                        scheduleTarget.kind === "meeting"
                        ? scheduleTarget.id
                        : null;
            }
            else if (scheduleProject) {
                // Generic project block — no task link, but the calendar will
                // resolve project context through other means (BU, etc.). For
                // backends that require a task link on Deep Focus we fall back
                // to a Flex block so the post doesn't 400.
                body.task_id = null;
                body.block_type = "flex";
            }
            await api.post("/time-blocks", body);
            notify.success("Scheduled", `${title} · ${duration}m`);
            // Reset search.
            setQuery("");
            setHits([]);
            setDropOpen(false);
            setExpandedHit(null);
            setPickedProjectTask(null);
            await onScheduled();
        }
        catch (err) {
            notify.error("Couldn't schedule", err instanceof Error ? err.message : undefined);
        }
        finally {
            setSaving(false);
        }
    }
    function onInputKey(e) {
        if (e.key === "Escape") {
            if (expandedHit) {
                setExpandedHit(null);
                setPickedProjectTask(null);
                return;
            }
            setQuery("");
            setDropOpen(false);
            inputRef.current?.blur();
        }
    }
    // Render helpers — schedule form (shared by task and project blocks)
    function renderScheduleForm() {
        return (_jsxs("div", { className: "cs-schedule", children: [_jsx("div", { className: "cs-schedule-h", children: "Schedule" }), loadingSlots && _jsx("div", { className: "cs-row-muted", children: "Finding free time\u2026" }), !loadingSlots && slots.length === 0 && (_jsxs("div", { className: "cs-row-muted", children: ["No free slots in the next 7 days that fit ", duration, " min. Use the custom time below."] })), _jsx("div", { className: "cs-chips", children: slots.map((s) => (_jsx("button", { type: "button", className: `cs-chip ${s.kind === "now" ? "primary" : ""}`, disabled: saving, onClick: () => void scheduleAt(s.start_at), children: s.label }, s.start_at))) }), _jsx("div", { className: "cs-schedule-h", style: { marginTop: 10 }, children: "Length" }), _jsx("div", { className: "cs-chips", children: DURATION_OPTIONS.map((m) => (_jsx("button", { type: "button", className: `cs-chip duration ${duration === m ? "active" : ""}`, onClick: () => setDuration(m), disabled: saving, children: m < 60 ? `${m}m` : m === 60 ? "1h" : `${m / 60}h` }, m))) }), _jsxs("div", { className: "cs-custom", children: [_jsx("input", { type: "date", value: customDate, onChange: (e) => setCustomDate(e.target.value), disabled: saving }), _jsx(TimeSelect, { value: customTime, onChange: setCustomTime, disabled: saving }), _jsx("button", { type: "button", className: "cs-confirm", disabled: saving || !customDate || !customTime, onClick: () => {
                                const dt = new Date(`${customDate}T${customTime}`);
                                if (Number.isNaN(dt.getTime())) {
                                    notify.error("Pick a valid date and time");
                                    return;
                                }
                                void scheduleAt(dt.toISOString());
                            }, children: saving ? "Adding…" : "Add to calendar" })] })] }));
    }
    // Expanded body for a dependency-blocked task — NO schedule form.
    // Shows the predecessor task + owners so the user knows who to
    // chase. Same look as renderTaskBody but with a yellow banner and
    // an owners list in place of the smart-slot chips.
    function renderBlockedBody(h) {
        const blockers = h.blockers ?? [];
        return (_jsxs("div", { className: "cs-expand-body cs-blocked-body", children: [_jsxs("div", { className: "cs-blocked-banner", children: [_jsx("span", { className: "ic", "aria-hidden": "true", children: "\u23F3" }), _jsxs("div", { children: [_jsx("div", { className: "cs-blocked-h", children: "Can't schedule yet" }), _jsx("div", { className: "cs-blocked-sub", children: "Predecessor work isn't done. Talk to the owner(s) below or wait until they close it out." })] })] }), blockers.length === 0 ? (_jsx("div", { className: "cs-row-muted", children: "Marked blocked but no predecessor detail loaded. Refresh search." })) : (_jsx("ul", { className: "cs-blocker-list", children: blockers.map((b) => (_jsxs("li", { className: "cs-blocker-row", children: [_jsx("div", { className: "cs-blocker-title", children: b.title }), _jsxs("div", { className: "cs-blocker-meta", children: [b.project_name && (_jsx("span", { className: "cs-blocker-proj", children: b.project_name })), b.assignees.length > 0 ? (_jsxs("span", { className: "cs-blocker-owners", children: ["Owned by", " ", b.assignees.map((a, i) => (_jsxs("span", { className: "cs-blocker-owner", children: [a.name, i < b.assignees.length - 1 ? ", " : ""] }, a.id)))] })) : (_jsx("span", { className: "cs-blocker-owners muted", children: "No assignees \u2014 manager / project owner is your contact." }))] })] }, b.task_id))) }))] }));
    }
    // The expanded body for a task pick (or a task picked inside a
    // project tree) — task metadata + schedule form.
    function renderTaskBody(title, meta) {
        return (_jsxs("div", { className: "cs-expand-body", children: [_jsxs("div", { className: "cs-task-card", children: [_jsx("div", { className: "cs-task-title", children: title }), meta && _jsx("div", { className: "cs-task-meta", children: meta })] }), renderScheduleForm()] }));
    }
    // The expanded body for a project pick — Mgmt-Assigned-style list of
    // the project's open tasks. Clicking a task switches to that task's
    // schedule form; a "Schedule generic project block" option sits at
    // the bottom for unlinked time.
    function renderProjectBody(hit) {
        const tree = projectTrees[hit.id];
        // If a task inside the project is picked, show its task-card +
        // schedule form, with a Back link to the task list.
        if (pickedProjectTask) {
            const meta = [
                hit.title,
                pickedProjectTask.due_date
                    ? `Due ${pickedProjectTask.due_date}`
                    : null,
            ]
                .filter(Boolean)
                .join(" · ");
            return (_jsxs("div", { className: "cs-expand-body", children: [_jsxs("button", { type: "button", className: "cs-back", onClick: () => setPickedProjectTask(null), children: ["\u2190 Back to ", hit.title, " tasks"] }), renderTaskBody(pickedProjectTask.title, meta)] }));
        }
        return (_jsxs("div", { className: "cs-expand-body", children: [_jsxs("div", { className: "cs-task-card", children: [_jsx("div", { className: "cs-task-title", children: hit.title }), _jsxs("div", { className: "cs-task-meta", children: [hit.due_date && _jsxs("span", { children: ["Due ", hit.due_date] }), hit.priority != null && _jsxs("span", { children: [" \u00B7 P", hit.priority] })] })] }), _jsx("div", { className: "cs-schedule-h", children: "Tasks in this project" }), projectLoading && (_jsx("div", { className: "cs-row-muted", children: "Loading tasks\u2026" })), !projectLoading && tree && tree.tasks.length === 0 && (_jsx("div", { className: "cs-row-muted", children: "No open tasks. Schedule a generic project block below." })), !projectLoading && tree && (_jsx("div", { className: "cs-project-tasks", children: tree.tasks
                        .filter((t) => t.status !== "completed")
                        .slice(0, 12)
                        .map((t) => (_jsxs("button", { type: "button", className: "cs-project-task", onClick: () => setPickedProjectTask(t), title: "Pick this task to schedule", children: [_jsx("span", { className: "cs-pt-title", children: t.title }), t.due_date && (_jsxs("span", { className: "cs-pt-due", children: ["Due ", t.due_date] })), _jsx("span", { className: "cs-pt-arrow", children: "\u2192" })] }, t.id))) })), _jsx("div", { className: "cs-schedule-h", style: { marginTop: 10 }, children: "Or schedule the project itself" }), renderScheduleForm()] }));
    }
    // ── Render ──────────────────────────────────────────────────────
    const showDropdown = dropOpen && (hits.length > 0 || query.trim() !== "");
    return (_jsxs("div", { className: "cal-search-root", style: { position: "relative" }, children: [_jsx("span", { className: "cal-search-ic", "aria-hidden": "true", children: "\u2315" }), _jsx("input", { ref: inputRef, type: "text", className: `cal-search-input ${showDropdown ? "active" : ""}`, placeholder: "Find a task or project to schedule\u2026", value: query, onChange: (e) => {
                    setQuery(e.target.value);
                    setExpandedHit(null);
                    setPickedProjectTask(null);
                }, onFocus: () => {
                    if (hits.length > 0)
                        setDropOpen(true);
                }, onKeyDown: onInputKey }), showDropdown && (_jsx("div", { className: "cal-search-drop", children: hits.length === 0 ? (_jsxs("div", { className: "ds-empty", children: ["No matches for \"", query, "\"."] })) : (_jsxs(_Fragment, { children: [hits.some((h) => h.kind !== "project") && (_jsx("div", { className: "ds-head", children: "Tasks" })), hits
                            .filter((h) => h.kind !== "project")
                            .map((h) => {
                            const isExpanded = expandedHit?.id === h.id && expandedHit?.kind === h.kind;
                            const blocked = !!h.is_blocked;
                            return (_jsxs("div", { className: "ds-group", children: [_jsxs("div", { className: `ds-item ${isExpanded ? "expanded" : ""} ${blocked ? "blocked" : ""}`, onClick: () => expandHit(h), children: [_jsx("div", { className: "ds-ic task", children: isExpanded ? "▾" : "▸" }), _jsxs("div", { className: "ds-body", children: [_jsx("div", { className: "ds-title", children: h.title }), _jsxs("div", { className: "ds-meta", children: [_jsx("span", { className: "ds-pill kind", children: KIND_LABEL[h.kind] }), blocked && (_jsxs("span", { className: "ds-pill blocked", title: "Has unfinished predecessors \u2014 can't schedule until they're done.", children: ["\u23F3 Waiting on ", h.blockers?.length ?? 0] })), h.project_name && _jsx("span", { children: h.project_name }), h.due_date && (_jsxs("span", { className: "ds-pill", children: ["Due ", h.due_date] })), h.priority != null && (_jsxs("span", { className: "ds-pill", children: ["P", h.priority] })), h.already_scheduled && (_jsx("span", { className: "ds-pill ok", children: "\uD83D\uDCC5 On your calendar" }))] })] })] }), isExpanded && blocked
                                        ? renderBlockedBody(h)
                                        : isExpanded &&
                                            renderTaskBody(h.title, [
                                                h.project_name,
                                                h.due_date ? `Due ${h.due_date}` : null,
                                                h.priority != null ? `P${h.priority}` : null,
                                            ]
                                                .filter(Boolean)
                                                .join(" · "))] }, `t:${h.kind}:${h.id}`));
                        }), hits.some((h) => h.kind === "project") && (_jsx("div", { className: "ds-head", children: "Projects" })), hits
                            .filter((h) => h.kind === "project")
                            .map((h) => {
                            const isExpanded = expandedHit?.id === h.id && expandedHit?.kind === "project";
                            return (_jsxs("div", { className: "ds-group", children: [_jsxs("div", { className: `ds-item ${isExpanded ? "expanded" : ""}`, onClick: () => expandHit(h), children: [_jsx("div", { className: "ds-ic project", children: isExpanded ? "▾" : "▸" }), _jsxs("div", { className: "ds-body", children: [_jsx("div", { className: "ds-title", children: h.title }), _jsxs("div", { className: "ds-meta", children: [_jsx("span", { className: "ds-pill kind", children: "Project" }), h.due_date && (_jsxs("span", { className: "ds-pill", children: ["Due ", h.due_date] })), h.priority != null && (_jsxs("span", { className: "ds-pill", children: ["P", h.priority] }))] })] })] }), isExpanded && renderProjectBody(h)] }, `p:${h.id}`));
                        })] })) }))] }));
}
